// =====================================================================
// SOLICITUDES DE DATOS PERSONALES · la bandeja de quien las atiende
// TODO-SIRVE F2 (30 sep 2026)
// =====================================================================
// Desde el 1 de septiembre de 2026 una persona podía pedir una copia, una
// corrección o el borrado de sus datos (`/api/arco`, hallazgo A-3), pero
// quien tenía que contestar no tenía dónde verlas: la cola existía solo en
// la base. Esta pantalla es esa cola, con lo más urgente arriba y la fecha
// límite pintada con los colores de siempre (amarillo cuando faltan cinco
// días o menos, rojo cuando ya pasó).
//
// CONTESTAR NO SE DESHACE. La respuesta se guarda y queda como hecho
// (`arco_hecho`, migración 155): es la prueba de que se contestó y cuándo.
// Un borrado se hace aparte, con acta, y tampoco se deshace; por eso los
// dos piden confirmación diciendo la consecuencia.
import { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, Inbox, Send, Trash2 } from 'lucide-react';
import { api, ErrorApi } from '../api';
import Ayuda from '../ui/Ayuda';
import ModalConfirmar from '../ui/ModalConfirmar';
import { Cargando, EstadoVacio, ErrorConReintento } from '../ui/EstadoDePantalla';
import { fechaCorta, diasParaLimite, colorDelPlazo } from './plazoArco';

const NOMBRE = {
  ACCESO: 'Copia de sus datos',
  RECTIFICACION: 'Corrección',
  CANCELACION: 'Borrado',
  OPOSICION: 'Oposición a un uso',
};

function textoDelPlazo(dias, iso) {
  if (dias === null) return 'Sin fecha límite';
  if (dias < 0) return `Venció el ${fechaCorta(iso)}`;
  if (dias === 0) return 'Vence hoy';
  return `Contestar antes del ${fechaCorta(iso)} (faltan ${dias} ${dias === 1 ? 'día' : 'días'})`;
}

function Contestar({ s, onListo, onCerrar }) {
  const [estado, setEstado] = useState('ATENDIDA');
  return (
    <ModalConfirmar
      titulo={`Contestar ${s.folio}`}
      descripcion="La respuesta queda guardada y no se edita después: es la prueba de que se contestó y cuándo. Escríbela como se la dirías a la persona."
      extra={(
        <fieldset className="space-y-1">
          <legend className="text-sm font-semibold text-gray-700">Cómo queda</legend>
          {[['ATENDIDA', 'Atendida: se hizo lo que pidió'], ['RECHAZADA', 'Rechazada: no procede, y la respuesta dice por qué']].map(([v, t]) => (
            <label key={v} className="flex items-center gap-2 text-sm min-h-[44px]">
              <input type="radio" name={`estado-${s.id}`} value={v} checked={estado === v} onChange={() => setEstado(v)} />
              {t}
            </label>
          ))}
        </fieldset>
      )}
      pedirMotivo
      etiquetaConfirmar="Guardar respuesta"
      onConfirmar={async (respuesta) => {
        await api.post(`/arco/solicitudes/${s.id}/resolver`, { estado, respuesta });
        onListo(`Respuesta guardada para ${s.folio}. La persona la ve en su cuenta, en «Mis solicitudes».`);
      }}
      onCerrar={onCerrar}
    />
  );
}

function Borrar({ s, onListo, onCerrar }) {
  return (
    <ModalConfirmar
      titulo={`Borrar los datos de ${s.titular || 'esta persona'}`}
      descripcion="Se vacían su nombre, sus correos, sus teléfonos, su foto y sus credenciales, y su cuenta deja de entrar. Sus movimientos quedan enteros, a nombre de un titular suprimido. No se deshace."
      pedirMotivo
      palabraConfirmacion="BORRAR"
      peligro
      etiquetaConfirmar="Borrar con acta"
      onConfirmar={async (motivo) => {
        const r = await api.post('/arco/suprimir', { id_usuario: s.id_usuario, id_solicitud: s.id, motivo });
        onListo(`${r.mensaje} Acta número ${r.acta}. Ahora contesta la solicitud ${s.folio} para cerrarla.`);
      }}
      onCerrar={onCerrar}
    />
  );
}

export default function SolicitudesDatosPersonales() {
  const [filtro, setFiltro] = useState('PENDIENTE');
  const [lista, setLista] = useState(null);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [contestando, setContestando] = useState(null);
  const [borrando, setBorrando] = useState(null);

  const cargar = useCallback(() => {
    api.get(`/arco/solicitudes?estado=${filtro === 'TODAS' ? '' : filtro}`)
      .then((l) => { setLista(l); setError(null); })
      .catch((e) => setError(e instanceof ErrorApi ? e.message : 'Revisa la conexión y vuelve a intentarlo.'));
  }, [filtro]);
  useEffect(cargar, [cargar]);

  function verFiltro(v) {
    if (v === filtro) return;
    setLista(null); setError(null); setFiltro(v);
  }
  function reintentar() {
    setLista(null); setError(null); cargar();
  }

  function listo(texto) {
    setAviso(texto);
    setContestando(null);
    setBorrando(null);
    cargar();
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <header className="space-y-1">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-[var(--color-marca)]" aria-hidden="true" />
          Solicitudes de datos personales
        </h1>
        <p className="text-sm text-gray-600">Lo que las personas de tu empresa pidieron sobre sus datos, con su folio y su fecha límite.</p>
      </header>
      <Ayuda id="datos_personales.bandeja.pantalla" />

      <div role="radiogroup" aria-label="Qué solicitudes ver" className="flex gap-1 bg-gray-100 rounded-lg p-0.5 w-fit">
        {[['PENDIENTE', 'Por contestar'], ['TODAS', 'Todas']].map(([v, t]) => (
          <button key={v} type="button" role="radio" aria-checked={filtro === v} onClick={() => verFiltro(v)}
                  className={`min-h-[44px] px-3 py-1.5 text-sm rounded-md ${filtro === v ? 'bg-white shadow font-semibold' : 'text-gray-600'}`}>
            {t}
          </button>
        ))}
      </div>

      {aviso && <p role="status" className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3">{aviso}</p>}
      {error && <ErrorConReintento que="No se pudo cargar la lista de solicitudes" porque={error} onReintentar={reintentar} />}
      {lista === null && !error && <Cargando filas={3} etiqueta="Cargando las solicitudes" />}

      {lista !== null && lista.length === 0 && (
        <EstadoVacio
          icono={Inbox}
          titulo={filtro === 'PENDIENTE' ? 'No hay solicitudes por contestar' : 'Todavía no hay solicitudes'}
          descripcion="Cuando alguien de tu empresa pida desde Mi cuenta una copia, una corrección o el borrado de sus datos, aparecerá aquí y te llegará un aviso a la campana."
          accion={filtro === 'PENDIENTE' ? (
            <button type="button" onClick={() => verFiltro('TODAS')} className="min-h-[44px] px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50">
              Ver también las ya contestadas
            </button>
          ) : null}
        />
      )}

      {lista !== null && lista.length > 0 && (
        <ul className="space-y-2">
          {lista.map((s) => {
            const dias = s.estado === 'PENDIENTE' ? diasParaLimite(s.responder_antes_de) : null;
            return (
              <li key={s.id} className="bg-white border border-gray-200 rounded-xl p-3 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono font-semibold text-gray-900">{s.folio}</span>
                  <span className="text-sm text-gray-800">{NOMBRE[s.tipo] || s.tipo}</span>
                  <span className="text-sm text-gray-600">· {s.titular || s.titular_dice || 'Persona sin cuenta'}</span>
                </div>
                <div className="text-xs text-gray-600">Enviada el {fechaCorta(s.creada_en)}</div>
                {s.detalle && <p className="text-sm text-gray-700 break-words">«{s.detalle}»</p>}
                {s.estado === 'PENDIENTE' ? (
                  <span className={`inline-block text-xs border rounded px-1.5 py-0.5 ${colorDelPlazo(dias)}`}>
                    {textoDelPlazo(dias, s.responder_antes_de)}
                  </span>
                ) : (
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">{s.estado === 'ATENDIDA' ? 'Atendida' : 'Rechazada'}</span>
                    {s.atendida_por_nombre ? ` por ${s.atendida_por_nombre}` : ''} el {fechaCorta(s.atendida_en)}: {s.respuesta}
                  </p>
                )}
                {s.estado === 'PENDIENTE' && (
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => { setAviso(null); setContestando(s); }}
                            className="min-h-[44px] inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-[var(--color-marca)] text-white font-semibold">
                      <Send className="h-4 w-4" aria-hidden="true" /> Contestar
                    </button>
                    {s.tipo === 'CANCELACION' && s.id_usuario && (
                      <button type="button" onClick={() => { setAviso(null); setBorrando(s); }}
                              className="min-h-[44px] inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-red-200 text-red-700">
                        <Trash2 className="h-4 w-4" aria-hidden="true" /> Borrar sus datos con acta
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {lista !== null && lista.length > 0 && <Ayuda id="datos_personales.bandeja.contestar" />}

      {contestando && <Contestar s={contestando} onListo={listo} onCerrar={() => setContestando(null)} />}
      {borrando && <Borrar s={borrando} onListo={listo} onCerrar={() => setBorrando(null)} />}
    </div>
  );
}
